import React, { useEffect, useState } from "react";
import { Download, FileText, Loader2, Eye, Layers } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";

const ACCENT = { unknown: "#64748b", d: "#d97706", e_or_below: "#b91c1c", check_consent: "#7c3aed" };

const download = async (path, fallbackName, setBusy, busyKey) => {
  setBusy(busyKey);
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("epc_token")}` }, credentials: "include",
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || `HTTP ${res.status}`);
    }
    const blob = await res.blob();
    const m = /filename="([^"]+)"/.exec(res.headers.get("content-disposition") || "");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = m ? m[1] : fallbackName;
    a.click();
    toast.success("Downloaded", { description: a.download });
  } catch (e) {
    toast.error("Download failed", { description: e.message });
  } finally {
    setBusy(null);
  }
};

const ListTile = ({ index, l, active, onView, busy, setBusy }) => {
  const q = `target_list=${l.key}`;
  const btn = "rounded-none h-7 px-2 text-[11px] border-slate-300";
  return (
    <div className="border-2 bg-white p-4 flex flex-col gap-2 transition-colors"
         style={{ borderColor: active ? ACCENT[l.key] : "#e2e8f0" }} data-testid={`target-list-${l.key}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: ACCENT[l.key] }}>List {index}</div>
      <div className="flex items-baseline justify-between gap-2">
        <div className="font-heading font-black text-lg" style={{ color: "var(--hi-olive)" }}>{l.title}</div>
        <div className="font-mono font-black text-2xl" data-testid={`target-list-count-${l.key}`}>{l.count.toLocaleString("en-GB")}</div>
      </div>
      <div className="text-[11px] text-slate-600 leading-snug flex-1">{l.desc}</div>
      <div className="flex flex-wrap gap-1.5 pt-1">
        <Button size="sm" variant={active ? "default" : "outline"} className={btn} onClick={() => onView(active ? null : l.key)} data-testid={`target-list-view-${l.key}`}>
          <Eye className="h-3 w-3 mr-1" /> {active ? "Showing" : "View"}
        </Button>
        {["pdf", "csv", "xlsx"].map((f) => (
          <Button key={f} size="sm" variant="outline" className={btn} disabled={!!busy}
                  onClick={() => download(`/properties/export?${q}&format=${f}`, `${l.key}.${f}`, setBusy, `${l.key}-${f}`)}
                  data-testid={`target-list-${f}-${l.key}`}>
            {busy === `${l.key}-${f}` ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Download className="h-3 w-3 mr-1" />}{f.toUpperCase()}
          </Button>
        ))}
      </div>
    </div>
  );
};

const Reconcile = ({ lists }) => {
  const [r, setR] = useState(null);
  useEffect(() => { api.get("/properties/target-lists/reconcile").then(({ data }) => setR(data)).catch(() => {}); }, [lists]);
  if (!r) return null;
  const n = (x) => x.toLocaleString("en-GB");
  return (
    <div className="mb-3 text-xs font-mono bg-slate-50 border border-slate-200 px-3 py-2" data-testid="target-lists-reconcile">
      Total {n(r.total)} = {n(r.below_c)} D-G "Below band C" (Dashboard) + {n(r.unknown)} Unknown band
      <span className="text-slate-500"> · Unknown: {n(r.unknown_clear)} clear (List 1) + {n(r.unknown_constrained)} in Check / Consent · D-G: {n(r.dg_clear)} clear (Lists 2-3) + {n(r.dg_constrained)} in Check / Consent</span>
    </div>
  );
};

export const TargetListsPanel = ({ activeKey, onView }) => {
  const [lists, setLists] = useState(null);
  const [busy, setBusy] = useState(null);
  useEffect(() => { api.get("/properties/target-lists").then(({ data }) => setLists(data.lists)).catch(() => {}); }, []);
  if (!lists) return null;
  return (
    <Card className="rounded-none p-5 border-2" style={{ borderColor: "var(--hi-olive)" }} data-testid="target-lists-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em]" style={{ color: "var(--hi-orange)" }}>
            <Layers className="h-4 w-4" /> Target lists · not in Saffron Asana
          </div>
          <p className="text-sm text-slate-700 mt-1">Asana is re-checked live on every download. Tree Preservation counts as clear; heritage designations and flood zones go to Check / Consent.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => download("/properties/target-pack.pdf", "Target-lists.pdf", setBusy, "pack-pdf")} disabled={!!busy}
                  className="rounded-none text-white" style={{ backgroundColor: "var(--hi-olive)" }} data-testid="target-pack-pdf-btn">
            {busy === "pack-pdf" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />} Full pack PDF
          </Button>
          <Button variant="outline" onClick={() => download("/properties/target-pack.xlsx", "Target-lists.xlsx", setBusy, "pack-xlsx")} disabled={!!busy}
                  className="rounded-none border-slate-300" data-testid="target-pack-xlsx-btn">
            {busy === "pack-xlsx" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />} Full pack Excel
          </Button>
        </div>
      </div>
      <Reconcile lists={lists} />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {lists.map((l, i) => (
          <ListTile key={l.key} index={i + 1} l={l} active={activeKey === l.key} onView={onView} busy={busy} setBusy={setBusy} />
        ))}
      </div>
    </Card>
  );
};
